import { ReactNode, useEffect, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { Grid } from './layout';

interface ExpandableContentProps {
  showText: string;
  hideText: string;
  forceOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children: ReactNode;
}

export default function ExpandableContent({
  showText,
  hideText,
  forceOpen,
  open: controlledOpen,
  onOpenChange,
  className,
  children,
}: ExpandableContentProps) {
  const [internalShowContent, setInternalShowContent] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined;
  const showContent = isControlled ? controlledOpen : internalShowContent;

  const toggleContent = () => {
    const newValue = !showContent;
    if (isControlled && onOpenChange) {
      onOpenChange(newValue);
    } else {
      setInternalShowContent(newValue);
    }
  };

  useEffect(() => {
    if (forceOpen) {
      if (isControlled && onOpenChange) {
        onOpenChange(true);
      } else {
        setInternalShowContent(true);
      }
    }
  }, [forceOpen, isControlled, onOpenChange]);

  return (
    <Grid gap='xl' cols='1' className={`mb-4 w-full mx-0 ${className}`}>
      <Button
        className={`w-full col-span-6 flex items-center gap-2 ${
          showContent ? 'bg-neutral-100 hover:bg-neutral-200' : ''
        }`}
        type='button'
        variant='outline'
        size='sm'
        onClick={toggleContent}
        buttonNamePosthog={ButtonNamePosthog.ToggleExpandableContent}
      >
        <span className='flex items-center gap-2 text-sm'>
          {showContent ? hideText : showText}
          {showContent ? (
            <ChevronUpIcon className='w-4 h-4' />
          ) : (
            <ChevronDownIcon className='w-4 h-4' />
          )}
        </span>
      </Button>
      <div className={`w-full col-span-6 ${showContent ? 'block' : 'hidden'}`}>
        {children}
      </div>
    </Grid>
  );
}
